import React, {useMemo, useState} from "react";
import {Box} from "@mui/material";

import {LoadingView} from "../../../../designSystem/components/LoadingView";
import {BaseInput} from "../../../../designSystem/components/BaseInput";
import {FilterBox} from "../../../../designSystem/components/FilterBox";
import {OptionButton} from "../../../../designSystem/components/OptionButton";
import {OrganizationCard} from "../../../../designSystem/components/OrganizationCard";

import TotalView from "../../../../components/TotalView/TotalView";
import {MultiSelection} from "../../../../components/MultiSelection/MultiSelection";
import {MapWithMarkers} from "../MapWithMarkers";
import {BackdropOverlay} from "../../../../components/BackdropOverlay";

import { PAGE_MODES } from "features/organizationList/model/pageMode";
import {filterMedicalOrganizations} from "../../model/filterMedicalOrganizations";
import {DEFAULT_FILTER, FILTERS} from "../../model/filters";
import {filterLocations} from "../../model/filterLocations";

import {ONLINE_RESERVATION_TYPES, ORGANIZATION_TYPE_OPTIONS, REGION_OPTIONS} from "../../../../utils/constants";
import {useMedicalOrganizations} from "../../hooks/useMedicalOrganizations";
import {useMedicalOrganizationFilter} from "../../hooks/useMedicalOrganizationFilter";

const DEFAULT_IMAGE = '/img/organization_placeholder.png'

export const OrganizationListFeature = () => {
  const [keyword, setKeyword] = useState<string>('');
  const [selectedFilter, setSelectedFilter] = useState<object>(DEFAULT_FILTER);
  const [selectedMedicalOrganizationId, setSelectedMedicalOrganizationId] = useState<string>('');
  const [mode, setMode] = useState<PAGE_MODES>(PAGE_MODES.LIST);

  const { isLoading, medicalOrganizations } = useMedicalOrganizations();
  const {
    selectedOrganizationType,
    selectedRegions,
    selectedOnlineReservation,
    setSelectedOrganizationType,
    setSelectedRegions,
    setSelectedOnlineReservation,
    isShowOrganizationTypeSelect,
    isShowRegionSelect,
    isShowOnlineReservationSelect,
    toggleOrganizationTypePanel,
    toggleRegionPanel,
    toggleOnlineReservationPanel,
    closeAllPanels,
    onOrganizationTypeSelectionCompleted,
    onRegionSelectionCompleted,
    onOnlineReservationSelectionCompleted,
    isOverlayVisible,
  } = useMedicalOrganizationFilter();

  const toggleView = () => {
    if (mode === PAGE_MODES.MAP) {
      setMode(PAGE_MODES.LIST);
      setSelectedMedicalOrganizationId('');
    } else {
      setMode(PAGE_MODES.MAP);
    }
  };

  const filteredMedicalOrganizations = useMemo(
    () => filterMedicalOrganizations({
      mode,
      medicalOrganizations,
      keyword,
      selectedOrganizationType,
      selectedRegions,
      selectedOnlineReservation,
      selectedFilter,
      selectedMedicalOrganizationId,
    }),
    [medicalOrganizations, keyword, selectedOrganizationType, selectedRegions, selectedOnlineReservation, selectedFilter, selectedMedicalOrganizationId, mode]
  )

  const filteredLocations = useMemo(
    () => filterLocations(filteredMedicalOrganizations),
    [filteredMedicalOrganizations]
  )

  if (isLoading)
    return <LoadingView />

  return (
    <Box height={"100%"} className={"flex flex-1 flex-col bg-gray-100"}>
      <Box className={"flex flex-row justify-between items-center gap-2 pl-4 pr-2 bg-white"}>
        <Box className={"flex-1"}>
          <BaseInput
            value={keyword}
            placeholder="請輸入關鍵字"
            onChange={(e: { target: { value: React.SetStateAction<string>; }; }) => {
              setKeyword(e.target.value)
              if (mode === PAGE_MODES.MAP) {
                setSelectedMedicalOrganizationId('')
              }
            }}
          />
        </Box>
        <TotalView numberOfData={filteredMedicalOrganizations.length}/>
        {/* Map Icon */}
        <Box onClick={toggleView} className="p-2 flex flex-col cursor-pointer items-center">
          <i className={`fas ${mode === 'list' ? 'fa-map' : 'fa-list'} text-gray-600`} style={{fontSize: '24px'}}></i>
          <p className={"text-sm"}>{mode === 'list' ? '地圖' : '列表'}</p>
        </Box>
      </Box>

      <Box className="relative">
        <Box className="px-2 pt-2 pb-3 z-20 shadow-md bg-white">
          <Box className="flex flex-row justify-evenly items-center gap-2">
            <FilterBox
              label="機構類別"
              selectedItems={selectedOrganizationType}
              isShowSelect={isShowOrganizationTypeSelect}
              onClick={toggleOrganizationTypePanel}
            />
            <FilterBox
              label="鄉鎮市"
              selectedItems={selectedRegions}
              isShowSelect={isShowRegionSelect}
              onClick={toggleRegionPanel}
            />
            <FilterBox
              label="線上掛號"
              selectedItems={selectedOnlineReservation}
              isShowSelect={isShowOnlineReservationSelect}
              onClick={toggleOnlineReservationPanel}
            />
          </Box>
          <Box className="flex flex-row pt-3 px-2 gap-3 overflow-x-scroll">
            {FILTERS.map(filter => (
              <OptionButton
                key={filter.label}
                label={filter.label}
                isSelected={selectedFilter[filter.key]}
                onClick={() => {
                  setSelectedFilter({
                    ...selectedFilter,
                    [filter.key]: !selectedFilter[filter.key]
                  })
                  setSelectedMedicalOrganizationId('')
                }}
              />
            ))}
          </Box>
        </Box>
        <MultiSelection
          key={'organizationType'}
          isShow={isShowOrganizationTypeSelect}
          options={ORGANIZATION_TYPE_OPTIONS}
          defaultSelected={['不限']}
          onChangeSelected={(values: React.SetStateAction<string[]>) => setSelectedOrganizationType(values)}
          onClickCompleted={onOrganizationTypeSelectionCompleted}
        />
        <MultiSelection
          key={'region'}
          isShow={isShowRegionSelect}
          options={REGION_OPTIONS}
          defaultSelected={['不限']}
          onChangeSelected={(values: React.SetStateAction<string[]>) => setSelectedRegions(values)}
          onClickCompleted={onRegionSelectionCompleted}
        />
        <MultiSelection
          key={'isShowOnlineReservation'}
          isShow={isShowOnlineReservationSelect}
          options={ONLINE_RESERVATION_TYPES}
          defaultSelected={['不限']}
          onChangeSelected={(values: React.SetStateAction<string[]>) => setSelectedOnlineReservation(values)}
          onClickCompleted={onOnlineReservationSelectionCompleted}
        />
      </Box>

      {isOverlayVisible && <BackdropOverlay onClick={closeAllPanels} />}

      <Box className={`
        flex flex-col px-4 py-2 overflow-y-scroll
        ${mode === PAGE_MODES.LIST ? 'h-[calc(100vh-225px)]' : 'h-[calc((100vh-225px)/2)]'}
      `}>
        {filteredMedicalOrganizations.map((medicalOrganization, ind) => (
          <OrganizationCard
            key={`${ind}`}
            imageUrl={medicalOrganization.coverImageUrl || DEFAULT_IMAGE}
            appointmentWeb={medicalOrganization.appointmentWeb || ''}
            progressWeb={medicalOrganization.progressWeb || ''}
            title={medicalOrganization.name}
            categories={[medicalOrganization.category]}
            region={medicalOrganization.region}
            address={medicalOrganization.address}
            phone={medicalOrganization.telephone}
            url={medicalOrganization.officialWeb}
            keyword={keyword.trim()}
            handleClickOnCard={() => {
              if (mode === PAGE_MODES.LIST) {
                window.open(medicalOrganization.officialWeb, '_blank')
              } else {
                setSelectedMedicalOrganizationId(medicalOrganization.id)
              }
            }}
          />
        ))}
      </Box>
      {mode === PAGE_MODES.MAP &&
        <MapWithMarkers
          locations={filteredLocations}
          onClickMarker={(id) => setSelectedMedicalOrganizationId(id)}
        />
      }
    </Box>
  )
};
